import React from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import FormHelperText from '@material-ui/core/FormHelperText';
import styles from './style';
import InputAdornment from '@material-ui/core/InputAdornment';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

class FancySearchSelectClass extends React.Component {
  state = {
    inputValue: '',
    anchorEl: null,
  };

  componentDidMount() {
    const selectedItem = this.getSelectedItem();
    if (selectedItem) {
      this.setState({ inputValue: this.getDisplay(selectedItem) });
    }
  }

  componentDidUpdate(prevProps) {
    const propsChanged =
      prevProps.currentVal !== this.props.currentVal ||
      prevProps.currentItem !== this.props.currentItem ||
      prevProps.items !== this.props.items;

    if (propsChanged) {
      const selectedItem = this.getSelectedItem();
      if (selectedItem && this.state.inputValue === '') {
        this.setState({ inputValue: this.getDisplay(selectedItem) });
      }
    }
  }

  isNotSet = () => {
    const { currentVal, currentItem, valAtr = 'id', baseOptionValAtr = -1 } = this.props;
    return (currentVal == null && currentItem == null) || currentItem[valAtr] === baseOptionValAtr;
  };

  getDisplay = (item) => {
    const { nameAtr = 'displayName' } = this.props;
    if (!item) return '';
    const raw = typeof nameAtr === 'function' ? nameAtr(item) : item[nameAtr];
    return raw != null ? String(raw) : '';
  };

  getSelectedItem = () => {
    const { currentVal, currentItem, items, valAtr = 'id' } = this.props;
    const val = typeof currentVal !== 'undefined' ? currentVal : (currentItem ? currentItem[valAtr] : undefined);
    if (!Array.isArray(items)) return null;
    return items.find(i => i[valAtr] === val) || null;
  };

  handleInputChange = (inputValue) => this.setState({ inputValue });

  handleSelect = (selectedItem) => {
    const { dispatchUpdate, propertyName, valAtr = 'id' } = this.props;
    const value = selectedItem[valAtr];
    dispatchUpdate({ propertyName, val: value });
    if (this.inputRef) {
      this.inputRef.blur();
    }
  };

  handleStateChange = (changes) => {
    if (changes.hasOwnProperty('isOpen') && !changes.isOpen) {
      const selectedItem = this.getSelectedItem();
      const { items, valAtr = 'id' } = this.props;
      const { inputValue } = this.state;
      const safeItems = Array.isArray(items) ? items : [];

      const filteredItems = safeItems.filter(item =>
        this.getDisplay(item).toLowerCase().includes((inputValue || '').toLowerCase())
      );

      if (filteredItems.length === 1 && inputValue.trim() !== '') {
        const oneItem = filteredItems[0];
        const isSame = selectedItem?.[valAtr] === oneItem[valAtr];

        if (!isSame) {
          this.handleSelect(oneItem);
          return;
        }
      }

      if (!inputValue && selectedItem) {
        this.setState({ inputValue: this.getDisplay(selectedItem) });
      }
    }
  };

  handleKeyDown = (event, filteredItems) => {
    if (event.key === 'Enter' && filteredItems.length === 1) {
      event.preventDefault();
      this.handleSelect(filteredItems[0]);
      if (this.inputRef) {
        this.inputRef.blur();
      }
    }
  };

  render() {
    const {
      items, label, placeholder, valAtr = 'id', classes,
      disabled = false, statusMessage = '',
    } = this.props;

    const { inputValue, anchorEl } = this.state;
    const selectedItem = this.getSelectedItem();
    const safeItems = Array.isArray(items) ? items : [];

    const filteredItems = safeItems.filter(item =>
      this.getDisplay(item).toLowerCase().includes((inputValue || '').toLowerCase())
    );

    const displayText = inputValue !== ''
      ? inputValue
      : (selectedItem ? this.getDisplay(selectedItem) : '');

    return (
      <Downshift
        selectedItem={selectedItem}
        inputValue={inputValue}
        onInputValueChange={this.handleInputChange}
        onChange={this.handleSelect}
        itemToString={item => (item ? this.getDisplay(item) : '')}
        onStateChange={this.handleStateChange}>
        {({
          getInputProps,
          getItemProps,
          getMenuProps,
          isOpen,
          openMenu,
        }) => (
          <div className={classes.downshiftContainer}>
            <TextField
              fullWidth
              variant="outlined"
              label={label}
              disabled={disabled}
              value={displayText}
              onClick={(e) => {
                const anchorEl = e.currentTarget;
                this.setState({
                  anchorEl,
                  inputValue: this.isNotSet() ? '' : this.state.inputValue,
                });
                openMenu();
              }}
              onFocus={(e) => {
                const anchorEl = e.currentTarget;
                this.setState({
                  anchorEl,
                  inputValue: this.isNotSet() ? '' : this.state.inputValue,
                });
                openMenu();
              }}
              InputProps={{
                ...getInputProps({
                  placeholder,
                  onChange: (e) => this.handleInputChange(e.target.value),
                  onKeyDown: (e) => this.handleKeyDown(e, filteredItems),
                  classes: { input: classes.fancySearchSelectInput },
                  endAdornment: (
                    <InputAdornment position="end">
                      <ArrowDropDownIcon className={classes.dropdownIcon} />
                    </InputAdornment>
                  ),
                }),
              }}
              style={{ marginTop: 16, }}
              inputRef={(node) => {
                if (node) {
                  this.inputRef = node;
                  if (!this.state.anchorEl) {
                    this.setState({ anchorEl: node });
                  }
                }
              }}
            />
            <Popper
              open={isOpen}
              anchorEl={anchorEl}
              placement="bottom-start"
              style={{ zIndex: 1300, width: anchorEl ? anchorEl.clientWidth : undefined }}>
              <Paper
                square
                className={classes.paper}
                style={{ maxHeight: 250, overflowY: 'auto' }}
                {...getMenuProps({}, { suppressRefError: true })}
              >
                {statusMessage && (
                  <FormHelperText className={classes.formHelperText}>
                    {statusMessage}
                  </FormHelperText>
                )}
                {filteredItems.map((item, index) => (
                  <MenuItem
                    key={item[valAtr]}
                    {...getItemProps({
                      item,
                      index,
                      selected: selectedItem?.[valAtr] === item[valAtr],
                      style: {textTransform: 'capitalize'}
                    })}
                  >
                    {this.getDisplay(item)}
                  </MenuItem>
                ))}
                {filteredItems.length === 0 && (
                  <MenuItem disabled style={{ fontStyle: 'italic' }}>
                    No matches
                  </MenuItem>
                )}
              </Paper>
            </Popper>
          </div>
        )}
      </Downshift>
    );
  }
}

FancySearchSelectClass.propTypes = {
  dispatchUpdate: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  propertyName: PropTypes.string.isRequired,
  currentItem: PropTypes.any,
  currentVal: PropTypes.any,
  items: PropTypes.array,
  valAtr: PropTypes.string,
  baseOptionValAtr: PropTypes.any,
  nameAtr: PropTypes.any,
  disabled: PropTypes.bool,
  statusMessage: PropTypes.string,
};

export const FancySearchSelect = withStyles(styles, { withTheme: true })(FancySearchSelectClass);
export default FancySearchSelect;
